/* @flow */
import * as React from 'react';
import { graphql } from 'react-apollo';
import { FormattedNumber } from 'react-intl';
import pluralize from 'pluralize';
import { once } from 'lodash';

import { formatData } from 'admin/lib/Chart';
import AdminHeadline from 'admin/components/AdminHeadline';
import AdminStatisticsCommunicationChart from 'admin/components/AdminStatisticsCommunicationChart';
import AdminStatisticsCommunicationChartHeadline from 'admin/components/AdminStatisticsCommunicationChartHeadline';
import ADMIN_STATISTICS_VIEW_QUERY from 'admin/queries/AdminStatisticsViewQuery.gql';
import AdminStatisticsReachChart from 'admin/components/AdminStatisticsReachChart';
import AdminStatisticsReachChartHeadline from 'admin/components/AdminStatisticsReachChartHeadline';
import Button from 'components/Button';
import Divider from 'components/Divider';
import Glyph from 'components/Glyph';
import Heading from 'components/Heading';
import Image from 'components/Image';
import LoadingIndicator from 'components/LoadingIndicator';
import MediaObject from 'components/MediaObject';
import OrganizationActions from 'actions/OrganizationActions';
import Swatch from 'components/Swatch';
import Table from 'components/Table';
import TabPanel from 'components/TabPanel';
import Tabs from 'components/Tabs';
import Text from 'components/Text';
import TitleHeader from 'components/TitleHeader';
import Tooltip from 'components/Tooltip';
import TooltipTrigger from 'components/TooltipTrigger';
import View from 'components/View';

import type { AdminStatisticsView_organizations } from 'admin/queries/__generated__/AdminStatisticsView';

import './styles/AdminStatisticsView.scss';

const DAY_TAB_LABEL = 'Daily';
const WEEK_TAB_LABEL = 'Weekly';
const MONTH_TAB_LABEL = 'Monthly';

function StatisticsHeading(props: { children: React.Node, tooltip?: string }) {
  const { children, tooltip } = props;

  return (
    <Heading level="3">
      <Text color="thunder">{children}</Text>{' '}
      {tooltip && (
        <TooltipTrigger placement="bottom" tooltip={<Tooltip>{tooltip}</Tooltip>}>
          <Glyph color="eclipse" name="help" size="small" />
        </TooltipTrigger>
      )}
    </Heading>
  );
}

function StatisticsTimeTabs(props: { children: React.Node }) {
  return (
    <Tabs buttonColor="polar-bear" buttonSize="xsmall" type="button">
      {props.children}
    </Tabs>
  );
}

type Props = {
  organization: ?AdminStatisticsView_organizations,
  orgId: number,
};

type State = {
  error: ?Error,
  // TODO: enforce a stricter schema once we know what stats we'll use long-term.
  stats: any,
};

class AdminStatisticsView extends React.Component<Props, State> {
  static defaultProps = {
    error: null,
    stats: null,
  };

  componentDidMount() {
    OrganizationActions.getDashboardStats(this.props.orgId)
      .then(stats => this.setState({ stats }))
      .catch(error => this.setState({ error }));
  }

  _logError = once(() => {
    console.error('Admin console had no stats');
  });

  getOrganizationType() {
    const { organization } = this.props;
    return organization && organization.type ? organization.type.toLowerCase() : '';
  }

  isSchool() {
    return this.getOrganizationType() === 'school';
  }

  renderCommunication() {
    const data = this.state.stats.communication;
    const { week, month, year } = data;
    const headline = {
      days: {
        announcements: week.chart[week.chart.length - 1].announcements,
        conversations: week.chart[week.chart.length - 1].conversations,
      },
      weeks: {
        announcements: month.chart[month.chart.length - 1].announcements,
        conversations: month.chart[month.chart.length - 1].conversations,
      },
      months: {
        announcements: year.chart[year.chart.length - 1].announcements,
        conversations: year.chart[year.chart.length - 1].conversations,
      },
    };
    const chartDataDays = formatData(week.chart, 'day');
    const chartDataWeeks = formatData(month.chart, 'week');
    const chartDataMonths = formatData(year.chart, 'month');
    let messagesDeliveredLastYear = 0;

    year.chart.forEach(yearMonth => {
      messagesDeliveredLastYear += yearMonth.conversations;
      messagesDeliveredLastYear += yearMonth.announcements;
    });

    return (
      <section className="AdminStatisticsView-section">
        <StatisticsHeading tooltip="A summary of Remind messages being sent">
          What does communication look like at your {this.getOrganizationType()}?
        </StatisticsHeading>
        <div className="AdminStatisticsView-chart">
          <StatisticsTimeTabs>
            <TabPanel title={<span>{DAY_TAB_LABEL}</span>}>
              <AdminStatisticsCommunicationChartHeadline
                data={headline.days}
                duration="yesterday"
              />
              <AdminStatisticsCommunicationChart data={chartDataDays} />
            </TabPanel>
            <TabPanel title={<span>{WEEK_TAB_LABEL}</span>}>
              <AdminStatisticsCommunicationChartHeadline
                data={headline.weeks}
                duration="last week"
              />
              <AdminStatisticsCommunicationChart data={chartDataWeeks} />
            </TabPanel>
            <TabPanel title={<span>{MONTH_TAB_LABEL}</span>}>
              <AdminStatisticsCommunicationChartHeadline
                data={headline.months}
                duration="last month"
                messagesDeliveredLastYear={messagesDeliveredLastYear}
              />
              <AdminStatisticsCommunicationChart data={chartDataMonths} />
            </TabPanel>
          </StatisticsTimeTabs>
        </div>
      </section>
    );
  }

  renderReachTeachers() {
    const data = this.state.stats.reach;
    const { week, month, year } = data;
    const headline = {
      days: {
        parents: week.chart[week.chart.length - 1].parents,
        students: week.chart[week.chart.length - 1].students,
        teachers: week.chart[week.chart.length - 1].teachers,
      },
      weeks: {
        parents: month.chart[month.chart.length - 1].parents,
        students: month.chart[month.chart.length - 1].students,
        teachers: month.chart[month.chart.length - 1].teachers,
      },
      months: {
        parents: year.chart[year.chart.length - 1].parents,
        students: year.chart[year.chart.length - 1].students,
        teachers: year.chart[year.chart.length - 1].teachers,
      },
    };
    const chartDataDays = formatData(week.chart, 'day');
    const chartDataWeeks = formatData(month.chart, 'week');
    const chartDataMonths = formatData(year.chart, 'month');

    return (
      <section>
        <StatisticsHeading>
          How many teachers are communicating at my {this.getOrganizationType()}?
        </StatisticsHeading>
        <div className="AdminStatisticsView-chart">
          <StatisticsTimeTabs>
            <TabPanel title={<span>{DAY_TAB_LABEL}</span>}>
              <AdminStatisticsReachChartHeadline
                data={headline.days}
                duration="yesterday"
                teachers
              />
              <AdminStatisticsReachChart data={chartDataDays} parents={false} students={false} />
            </TabPanel>
            <TabPanel title={<span>{WEEK_TAB_LABEL}</span>}>
              <AdminStatisticsReachChartHeadline
                data={headline.weeks}
                duration="last week"
                teachers
              />
              <AdminStatisticsReachChart data={chartDataWeeks} parents={false} students={false} />
            </TabPanel>
            <TabPanel title={<span>{MONTH_TAB_LABEL}</span>}>
              <AdminStatisticsReachChartHeadline
                data={headline.months}
                duration="last month"
                teachers
              />
              <AdminStatisticsReachChart data={chartDataMonths} parents={false} students={false} />
            </TabPanel>
          </StatisticsTimeTabs>
        </div>
      </section>
    );
  }

  renderReachSP() {
    const data = this.state.stats.reach;
    const { week, month, year } = data;
    const headline = {
      days: {
        parents: week.chart[week.chart.length - 1].parents,
        students: week.chart[week.chart.length - 1].students,
        teachers: week.chart[week.chart.length - 1].teachers,
      },
      weeks: {
        parents: month.chart[month.chart.length - 1].parents,
        students: month.chart[month.chart.length - 1].students,
        teachers: month.chart[month.chart.length - 1].teachers,
      },
      months: {
        parents: year.chart[year.chart.length - 1].parents,
        students: year.chart[year.chart.length - 1].students,
        teachers: year.chart[year.chart.length - 1].teachers,
      },
    };
    const chartDataDays = formatData(week.chart, 'day');
    const chartDataWeeks = formatData(month.chart, 'week');
    const chartDataMonths = formatData(year.chart, 'month');

    return (
      <section>
        <StatisticsHeading>
          How many students and parents are receiving messages at my {this.getOrganizationType()}?
        </StatisticsHeading>
        <div className="AdminStatisticsView-chart">
          <StatisticsTimeTabs>
            <TabPanel title={<span>{DAY_TAB_LABEL}</span>}>
              <AdminStatisticsReachChartHeadline data={headline.days} duration="yesterday" />
              <AdminStatisticsReachChart data={chartDataDays} teachers={false} />
            </TabPanel>
            <TabPanel title={<span>{WEEK_TAB_LABEL}</span>}>
              <AdminStatisticsReachChartHeadline data={headline.weeks} duration="last week" />
              <AdminStatisticsReachChart data={chartDataWeeks} teachers={false} />
            </TabPanel>
            <TabPanel title={<span>{MONTH_TAB_LABEL}</span>}>
              <AdminStatisticsReachChartHeadline data={headline.months} duration="last month" />
              <AdminStatisticsReachChart data={chartDataMonths} teachers={false} />
            </TabPanel>
          </StatisticsTimeTabs>
        </div>
      </section>
    );
  }

  renderPlatforms() {
    const { parents, students, teachers } = this.state.stats.platform.chart.roles;

    let appUsers = teachers.app + students.app + parents.app;
    let users =
      appUsers +
      teachers.web +
      teachers.text +
      teachers.email +
      students.web +
      students.text +
      students.email +
      parents.web +
      parents.text +
      parents.email;

    if (!appUsers) {
      appUsers = 0;
    }

    if (!users) {
      users = 0;
    }

    const activeUsersText = (
      <span>
        <Text weight="bold">
          <FormattedNumber value={users} /> active {pluralize('user', users)}
        </Text>{' '}
        last month
      </span>
    );

    let headline = <span>{activeUsersText}.</span>;

    if (users > 0 && appUsers > 0) {
      headline = (
        <span>
          Of {activeUsersText}, <Text weight="bold">{Math.round((appUsers / users) * 100)}%</Text>{' '}
          used the app.
        </span>
      );
    }

    return (
      <div>
        <header className="AdminStatisticsView-section-header">
          <StatisticsHeading tooltip="Which mediums your community is using to send messages">
            How are your teachers, students, and parents sending messages?
          </StatisticsHeading>
          <AdminHeadline>
            {headline}
            <br />
            Share the app with them so they can <Text weight="bold">translate</Text>,{' '}
            <Text weight="bold">make voice calls</Text>, and <Text weight="bold">more</Text>.
          </AdminHeadline>
        </header>

        <Table
          equalWidthColumns
          head={
            <tr>
              <th>Role</th>
              <th>Total active users</th>
              <th>Active on the app</th>
              <th>Active via web</th>
              <th>Active via text</th>
              <th>Active via email</th>
            </tr>
          }
          stripes
          className="AdminStatisticsView-platformsTable">
          <tbody>
            <tr>
              <td>
                <MediaObject center gutter="xsmall">
                  <div>
                    <Swatch color="sky" />
                  </div>
                  <span>Teacher</span>
                </MediaObject>
              </td>
              <td>{teachers.app + teachers.web + teachers.text + teachers.email}</td>
              <td>{teachers.app}</td>
              <td>{teachers.web}</td>
              <td>{teachers.text}</td>
              <td>{teachers.email}</td>
            </tr>
            <tr>
              <td>
                <MediaObject center gutter="xsmall">
                  <div>
                    <Swatch color="violet" />
                  </div>
                  <span>Student</span>
                </MediaObject>
              </td>
              <td>{students.app + students.web + students.text + students.email}</td>
              <td>{students.app}</td>
              <td>{students.web}</td>
              <td>{students.text}</td>
              <td>{students.email}</td>
            </tr>
            <tr>
              <td>
                <MediaObject center gutter="xsmall">
                  <div>
                    <Swatch color="caribbean" />
                  </div>
                  <span>Parent</span>
                </MediaObject>
              </td>
              <td>{parents.app + parents.web + parents.text + parents.email}</td>
              <td>{parents.app}</td>
              <td>{parents.web}</td>
              <td>{parents.text}</td>
              <td>{parents.email}</td>
            </tr>
          </tbody>
        </Table>
      </div>
    );
  }

  renderBody() {
    const { organization } = this.props;
    if (!this.state || !organization) {
      return <LoadingIndicator height={400} size="small" />;
    }

    if (this.state.error) {
      return (
        <Text align="center" element="div">
          Sorry—no statistics for your {this.getOrganizationType()} are available at this time.
        </Text>
      );
    }

    if (!organization.paid) {
      const alt = 'Admin Console preview';
      const imageSrc = this.isSchool()
        ? require('assets/images/admin/AdminStatisticsView/placeholders/school.svg')
        : require('assets/images/admin/AdminStatisticsView/placeholders/district.svg');

      return (
        <Text align="center" element="div">
          <Image src={imageSrc} alt={alt} />
        </Text>
      );
    }

    if (!this.state.stats) {
      this._logError();
      return (
        <TitleHeader
          image={require('assets/images/admin/desk.svg')}
          imageSize="xxsmall"
          title={`Your ${this.getOrganizationType()} has no activity yet`}
          subtext="Send a message to your teachers to get started. Stats take 24 hours to populate."
          action={
            <Button to="/messages/new" target="_blank">
              Send a message
            </Button>
          }
          flush
          nullState
          size="large"
        />
      );
    }

    return (
      <div>
        {this.renderCommunication()}
        <Divider size="xlarge" />
        {this.renderReachTeachers()}
        <Divider size="xlarge" />
        {this.renderReachSP()}
        <Divider size="xlarge" />
        {this.renderPlatforms()}
      </div>
    );
  }

  render() {
    return <View scroll>{this.renderBody()}</View>;
  }
}

const withData = graphql(ADMIN_STATISTICS_VIEW_QUERY, {
  options: ({ orgId }) => ({
    variables: {
      organizationId: orgId,
    },
  }),
  props: ({ data }) => ({
    organization: data.organizations ? data.organizations[0] : null,
  }),
});

export { AdminStatisticsView };
export default withData(AdminStatisticsView);
